import React from "react";
import { createPortal } from "react-dom";
import { ChildNavigationItem, NavigationItem } from "../Dashboard.types";
import { Link, Text, Translate } from "../../UI";

export interface CollapsedNavGroupProps {
    item: NavigationItem;
    /** Child items, already filtered for permissions / capabilities by the caller. */
    items: ChildNavigationItem[];
    /** Shared icon classes so the rail icon matches every other collapsed row. */
    iconClassName: string;
    rowClassName: string;
    onSelectSelf: () => void;
    onSelectChild: (child: ChildNavigationItem) => void;
}

const HOVER_CLOSE_DELAY_MS = 180;

const SubmenuItemStyles = "flex items-center gap-x-3 px-3 py-2 rounded-md text-[13px] font-semibold leading-5 cursor-pointer";

// #region CollapsedNavGroup
/**
 * A nav group on the collapsed rail. The 72px rail has no room for an indented
 * child tree, but the children still have to be reachable — so the group opens a
 * flyout listing them instead of hiding them.
 *
 * Opens on hover for mouse users and on click for touch and keyboard, and the
 * panel's first row links to the group itself, so every destination the expanded
 * sidebar offers is still reachable here.
 *
 * Rendered through a portal because the rail is `overflow-y: auto`, which would
 * clip a panel positioned inside it.
 */
export const CollapsedNavGroup: React.FunctionComponent<CollapsedNavGroupProps> = (props: CollapsedNavGroupProps): JSX.Element => {
    const { item, items, iconClassName, rowClassName, onSelectSelf, onSelectChild } = props;

    const [open, setOpen] = React.useState(false);
    const [position, setPosition] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);
    const panelRef = React.useRef<HTMLDivElement | null>(null);
    const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const label = item.fallbackLabel ?? item.name;

    const cancelClose = () => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    };

    // #region scheduleClose
    /**
     * Small delay so the pointer can cross the gap between the rail and the panel
     * without the panel vanishing mid-travel.
     */
    const scheduleClose = () => {
        cancelClose();
        closeTimer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
    };

    React.useEffect(() => cancelClose, []);

    // #region place
    const place = React.useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        setPosition({ top: rect.top, left: rect.right + 8 });
    }, []);

    const openPanel = () => {
        cancelClose();
        place();
        setOpen(true);
    };

    // Keep the panel inside the viewport once its real height is known — a group
    // near the bottom of the rail would otherwise run off the screen.
    React.useLayoutEffect(() => {
        if (!open || !panelRef.current) return;
        const rect = panelRef.current.getBoundingClientRect();
        const margin = 8;
        const maxTop = Math.max(margin, window.innerHeight - rect.height - margin);
        setPosition((prev) => (prev.top > maxTop ? { ...prev, top: maxTop } : prev));
    }, [open]);

    React.useEffect(() => {
        if (!open) return;

        const onPointerDown = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null;
            if (panelRef.current?.contains(target as Node)) return;
            if (triggerRef.current?.contains(target as Node)) return;
            setOpen(false);
        };
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };
        const onReflow = () => place();

        document.addEventListener('mousedown', onPointerDown, true);
        document.addEventListener('touchstart', onPointerDown, true);
        document.addEventListener('keydown', onKey, true);
        window.addEventListener('resize', onReflow);
        window.addEventListener('scroll', onReflow, true);

        return () => {
            document.removeEventListener('mousedown', onPointerDown, true);
            document.removeEventListener('touchstart', onPointerDown, true);
            document.removeEventListener('keydown', onKey, true);
            window.removeEventListener('resize', onReflow);
            window.removeEventListener('scroll', onReflow, true);
        };
    }, [open, place]);

    return (
        <li
            className="group flex w-full"
            onMouseEnter={openPanel}
            onMouseLeave={scheduleClose}
        >
            <button
                ref={triggerRef}
                type="button"
                aria-haspopup="true"
                aria-expanded={open}
                aria-label={label}
                title={label}
                onClick={() => (open ? setOpen(false) : openPanel())}
                onFocus={openPanel}
                className={rowClassName}
            >
                <item.icon className={iconClassName} aria-hidden="true" />
            </button>

            {open && createPortal(
                <div
                    ref={panelRef}
                    style={{ position: 'fixed', top: position.top, left: position.left }}
                    className="z-[60]"
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                >
                    <div className="w-56 max-w-[calc(100vw-16px)] rounded-lg p-2 shadow-xl ring-1 ring-gray-900/5 bg-surface-alt">
                        {/* The group's own destination, matching what its row navigates to when expanded. */}
                        <Link
                            href={item.href}
                            action={() => {
                                setOpen(false);
                                onSelectSelf();
                            }}
                            className={`${SubmenuItemStyles} ${item.current
                                ? 'bg-surface-strong text-black dark:bg-stone-700 dark:text-white'
                                : 'text-black dark:text-white hover:bg-surface-muted dark:hover:bg-stone-700'}`}
                        >
                            <item.icon className={iconClassName} aria-hidden="true" />
                            <Text className="!text-[13px] !leading-5">
                                <Translate translation_key={item.name} fallback={item.fallbackLabel} />
                            </Text>
                        </Link>

                        <div className="my-1 border-t border-stone-200 dark:border-stone-700" />

                        <ul className="space-y-0.5">
                            {items.map((child: ChildNavigationItem) => (
                                <li key={child.name}>
                                    <Link
                                        href={child.href}
                                        action={() => {
                                            setOpen(false);
                                            onSelectChild(child);
                                        }}
                                        className={`${SubmenuItemStyles} ${child.current
                                            ? 'bg-surface-strong text-black dark:bg-stone-700 dark:text-white'
                                            : 'text-gray-600 dark:text-gray-300 hover:bg-surface-muted dark:hover:bg-stone-700 hover:text-black dark:hover:text-white'}`}
                                    >
                                        <child.icon className={iconClassName} aria-hidden="true" />
                                        <Text className="!text-[13px] !leading-5">
                                            <Translate translation_key={child.name} fallback={child.fallbackLabel} />
                                        </Text>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>,
                document.body
            )}
        </li>
    );
};
